/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

// @flow

// Requires Places API (New) on the Maps key — see docs/site-address-typeahead-design.md.

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import debounce from 'lodash/fp/debounce.js';
import addressComponentsToFieldValues from '#support/models/places/addressComponentsToFieldValues.js';
import autocompleteSuggestionsRequest, {
  MIN_QUERY_LENGTH,
} from '#support/models/places/autocompleteSuggestionsRequest.js';
import typeaheadReducer, {
  dismissed,
  highlightAt,
  highlightNext,
  highlightPrevious,
  initialState,
  inputChanged,
  selected,
  selectionIndexForEnter,
  suggestionsFailed,
  suggestionsReceived,
} from '#support/models/places/typeaheadReducer.js';

const DEBOUNCE_MS = 300;

type Args = {|
  regionCode: ?string,
|};

const usePlacesSuggestions = ({ regionCode }: Args): Object => {
  const places = useMapsLibrary('places');
  const [state, dispatch] = useReducer(typeaheadReducer, initialState);
  const sessionTokenRef = useRef(null);
  const queryRef = useRef('');

  const sessionToken = useCallback(() => {
    if (!places) {
      return null;
    }
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }, [places]);

  const fetchSuggestions = useMemo(
    () =>
      debounce(DEBOUNCE_MS, query => {
        if (!places) {
          return;
        }
        const request = autocompleteSuggestionsRequest({
          input: query,
          regionCode,
          sessionToken: sessionToken(),
        });
        places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
          .then(({ suggestions }) => {
            dispatch(
              suggestionsReceived(
                query,
                (suggestions || []).map(s => s.placePrediction).filter(Boolean)
              )
            );
          })
          .catch(e => {
            // Logged, not surfaced: a misconfigured key is otherwise entirely invisible.
            /* eslint-disable-next-line no-console */
            console.error('Address autocomplete unavailable:', e);
            dispatch(suggestionsFailed(query));
          });
      }),
    [places, regionCode, sessionToken]
  );

  const cancelPendingFetch = useCallback(() => {
    if (fetchSuggestions.cancel) {
      fetchSuggestions.cancel();
    }
  }, [fetchSuggestions]);

  useEffect(() => cancelPendingFetch, [cancelPendingFetch]);

  const onQueryChange = useCallback(
    query => {
      queryRef.current = query;
      dispatch(inputChanged(query));
      if (query.length >= MIN_QUERY_LENGTH) {
        fetchSuggestions(query);
      } else {
        cancelPendingFetch();
      }
    },
    [cancelPendingFetch, fetchSuggestions]
  );

  const resolve = useCallback(
    prediction => {
      cancelPendingFetch();
      const place = prediction.toPlace();
      return place
        .fetchFields({ fields: ['addressComponents'] })
        .then(() => {
          // A selection ends the billed session.
          sessionTokenRef.current = null;
          dispatch(selected());
          return addressComponentsToFieldValues(place.addressComponents, regionCode);
        })
        .catch(() => {
          // Leave all four fields untouched rather than writing a partial address.
          dispatch(dismissed());
          return null;
        });
    },
    [cancelPendingFetch, regionCode]
  );

  const onDismiss = useCallback(() => {
    cancelPendingFetch();
    dispatch(dismissed());
  }, [cancelPendingFetch]);
  const highlightNextSuggestion = useCallback(() => dispatch(highlightNext()), []);
  const highlightPreviousSuggestion = useCallback(() => dispatch(highlightPrevious()), []);
  const highlightSuggestionAt = useCallback(index => dispatch(highlightAt(index)), []);

  return {
    state,
    onQueryChange,
    onDismiss,
    resolve,
    highlightNextSuggestion,
    highlightPreviousSuggestion,
    highlightSuggestionAt,
    enterSelectionIndex: () => selectionIndexForEnter(state),
  };
};

export default usePlacesSuggestions;
